import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import NedarimStandingOrderDtoIn from '@/models/NedarimStandingOrderDtoIn';
import TextRowsSkeleton from '@/components/ui/Skeleton/TextRowsSkeleton';
import {
  formatMoney,
  getActiveStatus,
  isCollecting,
  sumByCurrency,
} from './standingOrderModel';
import revenueIcon from '@/assets/icons/expected-revenue.svg';
import styles from './StandingOrdersSummary.module.scss';

interface StandingOrdersSummaryProps {
  /** The rows the table is showing, so the summary and the table can never disagree. */
  data: NedarimStandingOrderDtoIn[];
  /** Everything the fund has, to say how much the filters are hiding. */
  totalCount: number;
  /**
   * The board has not answered yet. Every figure here is counted from `data`, so before it
   * arrives they are all a truthful count of an empty list - and "0 פעילות" next to a table
   * that is still drawing its skeleton reads as "this fund has none", which is a different
   * statement altogether.
   */
  isLoading?: boolean;
}

/**
 * What the board adds up to, in one line.
 *
 * It was three cards the height of a table row. Chaya, looking at the screen Drook had just
 * called crowded: "the summary row just adds weight". Two of the three earned very little -
 * "shown of total" says something only while a filter is on, and "active" is a statistic
 * rather than something to act on - and the third, what the fund can expect to collect next
 * month, is the one figure on this board that answers a question about money and cannot be
 * read off the table at a glance.
 *
 * So the counts stay, quietly, and the money keeps the emphasis: a single line under the
 * heading, in the body-3 grey, with the expected revenue in the body text colour beside its
 * icon. Nothing is lost and the board starts at the filters.
 */
const StandingOrdersSummary = ({ data, totalCount, isLoading = false }: StandingOrdersSummaryProps) => {
  const { t, i18n } = useTranslation();

  const stats = useMemo(() => {
    let active = 0;

    for (const order of data) {
      if (getActiveStatus(order) === 'enabled') active++;
    }

    // Shekels and dollars are kept apart. Added together they made a number that was true of
    // no currency, and it was the figure a coordinator read as next month's income.
    const expectedRevenue = sumByCurrency(data.filter(isCollecting), (order) => order.amount);

    return { active, expectedRevenue };
  }, [data]);

  const revenueLabel = Object.entries(stats.expectedRevenue)
    .map(([currency, amount]) => formatMoney(amount, currency, i18n.language))
    .join(' · ') || formatMoney(0, 'ILS', i18n.language);

  /*
   * "128 of 602 shown" is worth saying while a filter hides something, and is noise when it
   * would only ever read "602 of 602".
   */
  const countLabel = data.length === totalCount
    ? t('standingOrders.summary.ordersCount', { count: totalCount })
    : t('standingOrders.summary.shownOfTotal', { shown: data.length, total: totalCount });

  return (
    <div className={`${styles.summaryLine} body-sm-regular`} data-testid="standing-orders-summary">
      {isLoading ? (
        <TextRowsSkeleton rows={[{ width: '280px', height: '14px' }]} />
      ) : (
        <>
          <span data-stat="total">{countLabel}</span>
          <span className={styles.separator} aria-hidden="true" />
          <span data-stat="active">
            {t('standingOrders.summary.activeCount', { count: stats.active })}
          </span>
          <span className={styles.separator} aria-hidden="true" />
          <span className={styles.revenue} data-stat="expectedRevenue">
            <img className={styles.revenueIcon} src={revenueIcon} alt="" />
            {t('standingOrders.summary.expectedRevenue')}
            <span className={`${styles.revenueValue} body-md-semibold`}>{revenueLabel}</span>
          </span>
        </>
      )}
    </div>
  );
};

export default StandingOrdersSummary;
